import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'

import { RootState } from '../../store'
import { InvestPresenter } from './invest-presenter'
import { projectServices, kycServices, investServices } from '../../services'
import {
  FileUpload,
  FileUploadPurpose,
  Investment,
  OpportunityDetails,
  UpdateInvestmentPayload,
  UserInvestmentDetails,
} from '../../lib/types'
import { isValidDocType, isValidFileSize } from '../../lib/utils/helpers'
import { showBanner } from '../../global-state/banner-slice'
import { captureEvent } from '../../lib/utils/posthogUtils/usePostHog'
import { EVENTS } from '../../lib/utils/constants'
import { EngagementEvents, logCrmEvent } from '../../lib/utils/crmEvents'
import './invest.scss'
import { PLATFORM_FEE, PLATFORM_FEE_CURRENCY } from '../payment/constants'
import ExtendLimitModal from '../../lib/components/ExtendLimitModal/extend-limit-modal'
import { usePendingInvestmentLimitRequest } from '../../lib/utils/use-pending-investment-limit-request'

const InvestContainer = () => {
  const navigate = useNavigate()
  const params = useParams()
  const [searchParams] = useSearchParams()
  const dispatch = useDispatch()
  const { projectId: opportunityParamId, projectSlug: opportunitySlug } = params
  const accessToken = searchParams.get('access')
  const { authToken, userId } = useSelector((state: RootState) => state.investor)
  const [opportunity, setOpportunity] = useState<OpportunityDetails>()
  const [userInvestmentDetail, setUserInvestmentDetail] = useState<UserInvestmentDetails>()
  const [investmentData, setInvestmentData] = useState<Investment>()
  const [currentStep, setCurrentStep] = useState(1)
  const [completedStep, setCompletedStep] = useState(0)
  const [investmentValue, setInvestmentValue] = useState<number>(0)
  const [investmentValueForTermsheet, setInvestmentValueForTermsheet] = useState<number>(0)
  const [isApiError, setIsApiError] = useState<boolean>(false)
  const [showContactUsModal, setContactUsModal] = useState(false)
  const [showExtendLimitModal, setShowExtendLimitModal] = useState(false)
  const { hasPendingRequest, markPending, openIfAllowed } = usePendingInvestmentLimitRequest()
  const [showInvestmentEssentialsModal, setInvestmentEssentialsModal] = useState(false)
  const [showTransactionChecklistModal, setTransactionChecklistModal] = useState(false)
  const [isTransactionChecklistChecked, setIsTransactionChecklistChecked] = useState(false)
  const [isTermsheetAgreementChecked, setIsTermsheetAgreementChecked] = useState(false)
  const [isTermsheetAgreementNeeded, setIsTermsheetAgreementNeeded] = useState(false)
  const [showTermsheetAgreementModal, setTermsheetAgreementModal] = useState(true)
  const [acceptTOBCheckbox, setAcceptTOBCheckbox] = useState(false)
  const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  // For link-only opportunities, ensure user is authenticated
  useEffect(() => {
    if (accessToken && opportunitySlug && !authToken) {
      // Link-only opportunity accessed without authentication
      navigate('/opportunities')
      return
    }
  }, [accessToken, opportunitySlug, authToken, navigate])

  const getInvestment = async () => {
    // For link-only opportunities, use opportunity.id; for regular ones, use opportunityParamId (from params)
    const opportunityIdForApi = accessToken ? opportunity?.id?.toString() : opportunityParamId
    if (!opportunityIdForApi) return

    const response = await investServices.getInvestmentByOpportunityId(
      opportunityIdForApi,
      'INITIATED',
      {
        Authorization: authToken ? authToken : '',
      },
    )

    if (response && response.data && response.status === 200) {
      setInvestmentData(response.data)
      const displayAmount = Number(response.data?.amount) / 100
      setInvestmentValue(displayAmount)
      setInvestmentValueForTermsheet(displayAmount)

      if (response.data.metadata) {
        setCompletedStep(Number(response.data.metadata.completedSteps))
        // Set currentStep to completed + 1 if this is initial load with existing progress
        if (investmentData === undefined && response.data.metadata.completedSteps > 0) {
          setCurrentStep(Number(response.data.metadata.completedSteps) + 1)
        }
      }
    }
  }

  const getUserInvestments = async () => {
    const response = await investServices.getUserInvestmentDetail_v1({
      Authorization: authToken ? authToken : '',
    })
    if (response && response.data && response.status === 200) {
      setUserInvestmentDetail(response.data)
    }
  }

  const fetchOpportunityById = async () => {
    let data
    if (accessToken && opportunitySlug) {
      // For link-only opportunities, ensure user is authenticated
      if (!authToken) {
        navigate('/opportunities')
        return
      }
      data = await projectServices.getOpportunityBySlugAndToken(
        opportunitySlug as string,
        accessToken,
      )
    } else {
      data = await projectServices.getOpportunityById(opportunityParamId as string, {
        Authorization: authToken ? authToken : '',
      })
    }
    if (data && data.data && data.status === 200) {
      setOpportunity(data.data)
      const displayTicketSize = Number(data.data.ticketSize) / 100
      setInvestmentValue(displayTicketSize || 0)
      if (data.data.metadata?.termsheet_agreement_template_url) {
        setIsTermsheetAgreementNeeded(true)
      }
    }
  }

  const postInvestmentAmount = async (payload: {
    projectId: string
    investmentAmount: number
    assetClass?: string
    metadata: { completedSteps: number }
  }) => {
    setIsTransitioning(true)
    try {
      const response = await investServices.postOpportunityInvestment(payload, {
        Authorization: authToken ? authToken : '',
      })
      if (response.status === 200) {
        setIsApiError(false)
        setCurrentStep((prev) => prev + 1)

        logCrmEvent(
          EngagementEvents.COMPLETED_FIRST_STEP_INVESTMENT,
          {
            Authorization: authToken ?? '',
          },
          {
            opportunityName: opportunity?.projectName,
            opportunityId: opportunity?.id,
          },
        )
      } else {
        const message = response.data.message || 'Error saving the investment'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
        setIsApiError(true)
      }
    } finally {
      setIsTransitioning(false)
    }
  }

  const updateInvestmentAmount = async (payload: UpdateInvestmentPayload) => {
    setIsTransitioning(true)
    try {
      const data = await investServices.updateOpportunityInvestment(payload, {
        Authorization: authToken ? authToken : '',
      })
      if (data && data.data && data.status === 200) {
        setInvestmentData(data.data)
        const displayAmount = Number(data.data?.amount) / 100
        setInvestmentValue(displayAmount)
        setIsApiError(false)
        setCurrentStep((prev) => prev + 1)
      } else {
        const message = data.data.message || 'Error saving the investment'
        dispatch(showBanner({ text: message, variant: 'error-banner' }))
        setIsApiError(true)
      }
    } finally {
      setIsTransitioning(false)
    }
  }

  const handleInvestmentAmount = async () => {
    // Determine the correct opportunity ID to use
    const opportunityIdStr = accessToken ? opportunity?.id?.toString() : opportunityParamId
    if (!opportunityIdStr) return

    if (!investmentData) {
      const payload = {
        projectId: opportunityIdStr,
        investmentAmount: investmentValue,
        assetClass: opportunity?.assetClass,
        metadata: { completedSteps: 1 },
      }
      await postInvestmentAmount(payload)
    } else if (investmentData.amount) {
      const opportunityIdNum = accessToken ? opportunity?.id : Number(opportunityParamId)
      if (!opportunityIdNum) return

      const payload = {
        projectId: opportunityIdNum,
        userId: userId,
        investmentId: investmentData.id,
        investmentAmount: investmentValue,
        assetClass: opportunity?.assetClass,
        metadata: { completedSteps: 1 },
      }
      await updateInvestmentAmount(payload)
    }
    await getUserInvestments()
  }
  const backToOpportunityHandler = () => {
    if (accessToken && opportunitySlug) {
      navigate(`/opportunity/link/${opportunitySlug}?access=${accessToken}`)
    } else if (opportunitySlug && opportunityParamId) {
      navigate(`/opportunity/${opportunitySlug}/${opportunityParamId}`)
    } else {
      navigate('/opportunities')
    }
  }

  const handleCurrentStep = (val: number) => {
    setCurrentStep(val)
  }

  const handleDisableButton = (): boolean => {
    const canInvest = userInvestmentDetail?.canInvest ?? '0'
    const canInvestAmount = investmentData?.amount
      ? parseInt(canInvest) / 100 + parseInt(investmentData?.amount) / 100
      : parseInt(canInvest) / 100
    if (isApiError) return true
    if (isTransitioning) return true
    if (showInvestmentEssentialsModal) return true
    if (
      currentStep === 1 &&
      !!opportunity?.ticketSize &&
      investmentValue >= parseInt(opportunity?.ticketSize) / 100 &&
      investmentValue <=
        (opportunity?.raiseGoal
          ? Math.min(
              (parseInt(opportunity?.raiseGoal) - parseInt(opportunity?.committedAmount ?? 0)) /
                100,
              canInvestAmount,
            )
          : canInvestAmount)
    )
      return false
    else if (currentStep === 2 && acceptTOBCheckbox) return false

    return true
  }

  const handleFileUpload = async (
    fileData: File,
    purpose?: FileUploadPurpose,
  ): Promise<{ data: FileUpload; status: number } | undefined> => {
    const isValidImage = isValidDocType(fileData)
    const isValidSize = isValidFileSize(fileData, 'doc')
    if (isValidImage && isValidSize) {
      const data = await projectServices.postUploadFiles(
        fileData,
        {
          Authorization: authToken ? authToken : '',
        },
        purpose,
      )
      if (data) return data
    } else {
      dispatch(
        showBanner({
          text: 'File size or type is incorrect for field',
          variant: 'error-banner',
        }),
      )
    }
  }

  const handleAgreementandBankDetailsConfirmation = async () => {
    setIsTransitioning(true)
    try {
      const payload = {
        investmentId: investmentData!.id,
        userId: investmentData!.investor_id,
        projectId: investmentData!.opportunity_id,
        assetClass: opportunity?.assetClass,
        metadata: {
          ...investmentData!.metadata,
          completedSteps: 2,
          bankDetailsConfirmed: true,
        },
      }
      const data = await investServices.updateOpportunityInvestment(payload, {
        Authorization: authToken ? authToken : '',
      })
      if (data && data.data && data.status === 200) {
        setInvestmentData(data.data)
        const displayAmount = Number(data.data?.amount) / 100
        setInvestmentValue(displayAmount)
        dispatch(
          showBanner({
            text: 'Agreement submitted successfully',
            variant: 'success-banner',
          }),
        )
        setIsApiError(false)
        setCurrentStep((prev) => prev + 1)
      } else {
        dispatch(
          showBanner({
            text: (data.data && data.data.message) || 'Something went wrong! Please try again.',
            variant: 'error-banner',
          }),
        )
        setIsApiError(true)
      }
    } finally {
      setIsTransitioning(false)
    }
  }

  const handleCompletePayment = async (swiftPaymentCopy: string, transactionNumber: string) => {
    if (!swiftPaymentCopy && !transactionNumber) return

    setIsTransitioning(true)
    try {
      const paymentCopy = {
        ...(swiftPaymentCopy && { paymentCopy: swiftPaymentCopy }),
        ...(transactionNumber && { transactionNumber }),
      }

      const payload = {
        userId: investmentData!.investor_id,
        projectId: investmentData!.opportunity_id,
        investmentId: investmentData!.id,
        assetClass: opportunity?.assetClass,
        status: 'ORDER_PLACED',
        metadata: {
          ...investmentData!.metadata,
          completedSteps: 3,
          ...paymentCopy,
        },
        platformFee: {
          amount: PLATFORM_FEE,
          currency: PLATFORM_FEE_CURRENCY,
        },
      }

      const data = await investServices.updateOpportunityInvestment(payload, {
        Authorization: authToken ? authToken : '',
      })
      if (data && data.data && data.status === 200) {
        setInvestmentData(data.data)
        const displayAmount = Number(data.data?.amount) / 100
        setInvestmentValue(displayAmount)
        dispatch(
          showBanner({
            text: 'Payment details submitted successfully',
            variant: 'success-banner',
          }),
        )
        setIsApiError(false)
        navigateToPaymentSuccessScreen()

        captureEvent(EVENTS.investment_application_submitted, { data })
        logCrmEvent(
          EngagementEvents.PAYMENT_PROCESSED_SWIFT_COPY_UPLOADED,
          {
            Authorization: authToken ? authToken : '',
          },
          {
            opportunityName: opportunity?.projectName,
            opportunityId: opportunity?.id,
            orderId: investmentData?.order_id,
          },
        )
      } else setIsApiError(true)
    } finally {
      setIsTransitioning(false)
    }
  }

  const navigateToPaymentSuccessScreen = () => {
    // For link-only opportunities, use opportunity.id; for regular ones, use opportunityParamId
    const opportunityIdForUrl = accessToken ? opportunity?.id?.toString() : opportunityParamId
    navigate(`/payment/${opportunitySlug}/${opportunityIdForUrl}/submitted-successful`)
  }

  const handleShowContactUsModal = () => setContactUsModal(!showContactUsModal)
  const handleShowExtendLimitModal = () => {
    void openIfAllowed(() => setShowExtendLimitModal(true))
  }

  useEffect(() => {
    const popup = window.open('', 'popup', 'width=1,height=1')
    setShowPopupBlockedModal(!popup)
    popup?.close()
  }, [])

  useEffect(() => {
    // Only run initial setup once
    if (
      (opportunityParamId !== 'undefined' && opportunitySlug !== 'undefined') ||
      (accessToken && opportunitySlug !== 'undefined')
    ) {
      fetchOpportunityById()
      getUserInvestments()
      // For regular opportunities, get investment immediately
      if (opportunityParamId && !accessToken) {
        getInvestment()
      }
    } else {
      navigate('/')
    }
  }, [])

  // Refresh investment data when step changes
  useEffect(() => {
    getInvestment()
  }, [currentStep])

  // For link-only opportunities, call getInvestment when opportunity is loaded
  useEffect(() => {
    if (accessToken && opportunity?.id) {
      getInvestment()
    }
  }, [opportunity?.id, accessToken])

  useEffect(() => {
    const getUserInvestmentLatest = async () => {
      // For link-only opportunities, use opportunity.id; for regular ones, use opportunityParamId
      const opportunityId = accessToken
        ? opportunity?.id
        : opportunityParamId
        ? parseInt(opportunityParamId)
        : 0
      if (!opportunityId) return

      const response = await investServices.getInvestmentLatest_v1(
        { opportunityId },
        {
          Authorization: authToken ? authToken : '',
        },
      )
      if (response && response.data && response.status === 200) {
        if (response.data.status === 'ORDER_PLACED') navigate(-1)
      }
    }
    const getKYC = async () => {
      const response = await kycServices.getKYC_v1(userId.toString(), {
        Authorization: authToken ? authToken : '',
      })
      if (!!response && response.data && response.status === 200) {
        if (response.data.status !== 'APPROVED') {
          dispatch(
            showBanner({
              text: 'Please complete the KYC for investing in the opportunities.',
              variant: 'error-banner',
            }),
          )
          navigate('/profile/kyc')
        }

        if (!response.data.investment_essentials_agreement) setInvestmentEssentialsModal(true)
        setIsTransactionChecklistChecked(!!response.data.transaction_checklist_agreement)
        if (response.data.accept_tob) setAcceptTOBCheckbox(true)
      }
    }
    if (authToken) {
      getKYC()
      getUserInvestmentLatest()
    }
  }, [])

  return (
    <>
      <InvestPresenter
        backToProjectHandler={backToOpportunityHandler}
        opportunity={opportunity!}
        currentStep={currentStep}
        handleCurrentStep={handleCurrentStep}
        userInvestmentDetail={userInvestmentDetail!}
        investmentValue={investmentValue}
        setInvestmentValue={setInvestmentValue}
        investmentValueForTermsheet={investmentValueForTermsheet}
        investmentData={investmentData!}
        handleDisableButton={handleDisableButton}
        handleInvestmentAmount={handleInvestmentAmount}
        handleFileUpload={handleFileUpload}
        handleCompletePayment={handleCompletePayment}
        navigateToPaymentSuccessScreen={navigateToPaymentSuccessScreen}
        handleAgreementandBankDetailsConfirmation={handleAgreementandBankDetailsConfirmation}
        completedStep={completedStep}
        setIsApiError={setIsApiError}
        showContactUsModal={showContactUsModal}
        handleShowContactUsModal={handleShowContactUsModal}
        handleShowExtendLimitModal={handleShowExtendLimitModal}
        hasPendingLimitRequest={hasPendingRequest}
        showInvestmentEssentialsModal={showInvestmentEssentialsModal}
        setInvestmentEssentialsModal={setInvestmentEssentialsModal}
        isTransactionChecklistChecked={isTransactionChecklistChecked}
        setIsTransactionChecklistChecked={setIsTransactionChecklistChecked}
        showTransactionChecklistModal={showTransactionChecklistModal}
        setTransactionChecklistModal={setTransactionChecklistModal}
        isTermsheetAgreementChecked={isTermsheetAgreementChecked}
        setIsTermsheetAgreementChecked={setIsTermsheetAgreementChecked}
        showTermsheetAgreementModal={showTermsheetAgreementModal}
        setTermsheetAgreementModal={setTermsheetAgreementModal}
        isTermsheetAgreementNeeded={isTermsheetAgreementNeeded}
        acceptTOBCheckbox={acceptTOBCheckbox}
        setAcceptTOBCheckbox={setAcceptTOBCheckbox}
        showPopupBlockedModal={showPopupBlockedModal}
        isTransitioning={isTransitioning}
      />
      {showExtendLimitModal ? (
        <ExtendLimitModal
          onClose={() => setShowExtendLimitModal(false)}
          onSubmitted={markPending}
        />
      ) : null}
    </>
  )
}

export { InvestContainer }
